import { useEffect, useRef } from "react";
import { createChart, CandlestickSeries, createSeriesMarkers } from "lightweight-charts";
import { chartTheme, CANDLE_COLORS } from "@/components/chartTheme";
import { useTheme } from "@/context/ThemeContext";

// One trade on 1-minute candles: entry/exit arrows plus labelled entry, stop and target lines.
// Colours come from the ops CSS variables so light and dark themes both match the dashboard.

export const cssHsl = (el, name, fallback) => {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v ? `hsl(${v.split(/\s+/).join(", ")})` : fallback;
};

// nearest candle time (markers must sit on an existing bar); entryUnix/exitUnix are unix seconds
const snap = (times, t) => {
  if (t == null || !times.length || t < times[0] - 60 || t > times[times.length - 1] + 60) return null;
  let lo = 0, hi = times.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < t) lo = mid + 1; else hi = mid;
  }
  if (lo > 0 && Math.abs(times[lo - 1] - t) <= Math.abs(times[lo] - t)) return times[lo - 1];
  return times[lo];
};

export default function TradeChart({ candles, direction, entryUnix, exitUnix, entryPrice, exitPrice, stop, target, height = 300 }) {
  const ref = useRef(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const el = ref.current;
    if (!el || !candles?.length) return undefined;
    const base = chartTheme(isDark);
    const chart = createChart(el, {
      ...base,
      rightPriceScale: { ...base.rightPriceScale, scaleMargins: { top: 0.1, bottom: 0.1 } },
      handleScroll: { vertTouchDrag: false },   // let the page scroll on phones
      width: el.clientWidth,
      height,
    });
    const extras = [entryPrice, exitPrice, stop, target].filter((v) => typeof v === "number" && isFinite(v));
    const series = chart.addSeries(CandlestickSeries, {
      ...CANDLE_COLORS,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      priceLineVisible: false,
      lastValueVisible: false,
      // keep the stop and target lines inside the visible price range
      autoscaleInfoProvider: (original) => {
        const r = original();
        if (!r || !extras.length) return r;
        return { ...r, priceRange: {
          minValue: Math.min(r.priceRange.minValue, ...extras),
          maxValue: Math.max(r.priceRange.maxValue, ...extras),
        } };
      },
    });
    series.setData(candles);

    const long = direction === "LONG";
    const cLong = cssHsl(el, "--ops-long", "#22c55e");
    const cShort = cssHsl(el, "--ops-short", "#ef4444");
    const cBrand = cssHsl(el, "--ops-brand", "hsl(178, 84%, 40%)");
    const cMuted = cssHsl(el, "--ops-text-muted", "hsl(220, 10%, 70%)");
    const line = (price, color, title, style) => {
      if (typeof price !== "number" || !isFinite(price)) return;
      series.createPriceLine({ price, color, title, lineWidth: 1, lineStyle: style, axisLabelVisible: true });
    };
    line(entryPrice, cMuted, "Entry", 0);
    line(stop, cShort, "Stop", 2);
    line(target, cBrand, "Target", 2);

    const times = candles.map((c) => c.time);
    const markers = [];
    const tIn = snap(times, entryUnix);
    if (tIn != null && typeof entryPrice === "number") {
      markers.push({ time: tIn, position: long ? "atPriceBottom" : "atPriceTop", price: entryPrice,
        shape: long ? "arrowUp" : "arrowDown", color: long ? cLong : cShort, text: long ? "Buy" : "Sell" });
    }
    const tOut = snap(times, exitUnix);
    if (tOut != null && typeof exitPrice === "number") {
      markers.push({ time: tOut, position: long ? "atPriceTop" : "atPriceBottom", price: exitPrice,
        shape: long ? "arrowDown" : "arrowUp", color: cMuted, text: "Exit" });
    }
    markers.sort((a, b) => a.time - b.time);
    try { if (markers.length) createSeriesMarkers(series, markers); } catch (e) { /* chart still shows the lines */ }

    chart.timeScale().fitContent();
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) if (e.contentRect.width > 0) chart.applyOptions({ width: e.contentRect.width });
    });
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); };
  }, [candles, direction, entryUnix, exitUnix, entryPrice, exitPrice, stop, target, height, isDark]);

  return <div ref={ref} data-testid="trade-detail-chart" style={{ height }} />;
}
